/**
 * Office Preview Service
 * Handles Microsoft 365 online document previews for authenticated users
 * Supports Word (.docx), Excel (.xlsx), PowerPoint (.pptx) files
 */

/**
 * Office Preview Service Class
 * Provides methods to generate Office 365 preview URLs for documents
 */
export class OfficePreviewService {
    /**
     * Office file extensions that support online preview
     */
    static SUPPORTED_EXTENSIONS = {
        word: ['doc', 'docx', 'docm', 'dot', 'dotx', 'dotm'],
        excel: ['xls', 'xlsx', 'xlsm', 'xlt', 'xltx', 'xltm', 'xlsb', 'csv'],
        powerpoint: ['ppt', 'pptx', 'pptm', 'pot', 'potx', 'potm', 'pps', 'ppsx', 'ppsm'],
        onenote: ['one']
    };

    /**
     * Get file extension from filename
     * @param {string} fileName - File name
     * @returns {string} File extension (lowercase)
     */
    static getFileExtension(fileName) {
        if (!fileName || !fileName.includes('.')) return '';
        return fileName.substring(fileName.lastIndexOf('.') + 1).toLowerCase();
    }

    /**
     * Check if file is an Office document that supports preview
     * @param {string} fileName - File name
     * @returns {boolean} True if file supports Office preview
     */
    static isOfficeDocument(fileName) {
        const ext = this.getFileExtension(fileName);
        return Object.values(this.SUPPORTED_EXTENSIONS)
            .flat()
            .includes(ext);
    }

    /**
     * Get the Office document type
     * @param {string} fileName - File name
     * @returns {string|null} Document type ('word', 'excel', 'powerpoint', 'onenote') or null
     */
    static getDocumentType(fileName) {
        const ext = this.getFileExtension(fileName);
        
        for (const [type, extensions] of Object.entries(this.SUPPORTED_EXTENSIONS)) {
            if (extensions.includes(ext)) {
                return type;
            }
        }
        
        return null;
    }

    /**
     * Upload document to SharePoint/OneDrive and get preview URL via Microsoft Graph
     * Note: This requires the document to be accessible via a public URL or SharePoint
     * 
     * @param {string} accessToken - MSAL access token with Files.ReadWrite scope
     * @param {Blob} fileBlob - File blob
     * @param {string} fileName - File name
     * @returns {Promise<Object>} Object with webUrl and previewUrl
     */
    static async uploadToOneDriveAndGetPreview(accessToken, fileBlob, fileName) {
        try {
            // Upload file to OneDrive
            const uploadUrl = `https://graph.microsoft.com/v1.0/me/drive/root:/temp-previews/${fileName}:/content`;
            
            const uploadResponse = await fetch(uploadUrl, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/octet-stream'
                },
                body: fileBlob
            });

            if (!uploadResponse.ok) {
                throw new Error(`Failed to upload to OneDrive: ${uploadResponse.statusText}`);
            }

            const uploadData = await uploadResponse.json();
            
            // Get preview URL
            const previewUrl = `https://graph.microsoft.com/v1.0/me/drive/items/${uploadData.id}/preview`;
            
            const previewResponse = await fetch(previewUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    viewer: 'office',
                    chromeless: false
                })
            });

            if (!previewResponse.ok) {
                throw new Error(`Failed to get preview URL: ${previewResponse.statusText}`);
            }

            const previewData = await previewResponse.json();
            
            return {
                webUrl: uploadData.webUrl,
                previewUrl: previewData.getUrl,
                itemId: uploadData.id,
                fileName: fileName
            };
        } catch (error) {
            console.error('Failed to upload and get preview:', error);
            throw error;
        }
    }

    /**
     * Create Office Online preview URL using Office Web Apps viewer
     * This method works with publicly accessible URLs
     * 
     * @param {string} documentUrl - Publicly accessible URL of the document
     * @param {string} fileName - File name (to determine document type)
     * @returns {string} Office Online preview URL
     */
    static createOfficeOnlinePreviewUrl(documentUrl, fileName) {
        const docType = this.getDocumentType(fileName);
        
        if (!docType) {
            throw new Error('Unsupported document type');
        }

        // Use Office Online viewer (works for publicly accessible documents)
        const encodedUrl = encodeURIComponent(documentUrl);
        
        // Office Online viewer endpoints
        const viewerUrls = {
            word: `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`,
            excel: `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`,
            powerpoint: `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`
        };

        return viewerUrls[docType] || null;
    }

    /**
     * Open Office document in preview mode
     * This method attempts to use Microsoft Graph API first, then falls back to Office Online viewer
     * 
     * @param {string} msalAccessToken - MSAL access token
     * @param {Blob} fileBlob - File blob
     * @param {string} fileName - File name
     * @param {string} [publicUrl] - Optional public URL if document is publicly accessible
     * @returns {Promise<void>}
     */
    static async openPreview(msalAccessToken, fileBlob, fileName, publicUrl = null) {
        try {
            // Check if it's an Office document
            if (!this.isOfficeDocument(fileName)) {
                throw new Error('This file type does not support Office preview');
            }

            // Strategy 1: If we have a public URL, use Office Online viewer (simplest)
            if (publicUrl) {
                const previewUrl = this.createOfficeOnlinePreviewUrl(publicUrl, fileName);
                window.open(previewUrl, '_blank', 'width=1200,height=800');
                return;
            }

            // Strategy 2: Upload to OneDrive and get preview URL via Graph API
            // This requires Files.ReadWrite scope
            try {
                const preview = await this.uploadToOneDriveAndGetPreview(
                    msalAccessToken,
                    fileBlob,
                    fileName
                );
                
                // Open the preview URL
                window.open(preview.previewUrl, '_blank', 'width=1200,height=800');
            } catch (graphError) {
                console.error('Graph API preview failed:', graphError);
                
                // Strategy 3: Fallback - create temporary blob URL and try Office viewer
                // Note: This may not work if document requires authentication
                const blobUrl = URL.createObjectURL(fileBlob);
                const previewUrl = this.createOfficeOnlinePreviewUrl(blobUrl, fileName);
                const previewWindow = window.open(previewUrl, '_blank', 'width=1200,height=800');
                
                // Clean up after some time
                setTimeout(() => {
                    URL.revokeObjectURL(blobUrl);
                }, 60000); // 1 minute

                if (!previewWindow) {
                    throw new Error('Popup blocked. Please allow popups for this site.');
                }
            }
        } catch (error) {
            console.error('Failed to open Office preview:', error);
            throw error;
        }
    }

    /**
     * Get preview information without opening
     * Useful for showing preview availability to users
     * 
     * @param {string} fileName - File name
     * @returns {Object} Preview information
     */
    static getPreviewInfo(fileName) {
        const isSupported = this.isOfficeDocument(fileName);
        const docType = this.getDocumentType(fileName);
        
        return {
            isSupported,
            documentType: docType,
            previewMethod: isSupported ? 'Office 365 Online' : 'Standard Browser',
            requiresAuth: true,
            description: isSupported 
                ? `Preview this ${docType} document in Microsoft 365 Online` 
                : 'Preview not available for this file type'
        };
    }

    /**
     * Clean up temporary files from OneDrive
     * Call this periodically to remove temporary preview files
     * 
     * @param {string} accessToken - MSAL access token
     * @param {string} itemId - OneDrive item ID
     * @returns {Promise<void>}
     */
    static async cleanupTempFile(accessToken, itemId) {
        try {
            const deleteUrl = `https://graph.microsoft.com/v1.0/me/drive/items/${itemId}`;
            
            await fetch(deleteUrl, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`
                }
            });
        } catch (error) {
            console.error('Failed to cleanup temp file:', error);
            // Don't throw - cleanup is best effort
        }
    }
}

// Export the service
export default OfficePreviewService;
